/**
 * Combined Transitions & Processes View
 * 
 * Shows transitions and their associated processes together in one unified list
 * Allows viewing/editing either the transition or the process
 */

import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Tag, 
  Input, 
  Space, 
  Tooltip,
  Badge,
  message,
  Spin,
  Divider,
  Modal,
  Form,
  Select,
  Checkbox,
  Alert
} from 'antd';
import {
  ArrowRightOutlined,
  EditOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  SearchOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import type { ColumnsType } from 'antd/es/table';
import { ArtifactStateFlowDiagram } from './ArtifactStateFlowDiagram';
import { EditTransitionDialog } from './EditTransitionDialog';
import { EditProcessDialog } from './EditProcessDialog';
import styles from './TransitionsProcessesView.module.css';

const { Search } = Input;

interface TransitionProcessRow {
  transition_id: string;
  transition_name: string;
  from_state_name: string;
  to_state_name: string;
  transition_role: string;
  has_process: boolean;
  process_id?: string;
  process_name?: string;
  process_type?: string;
  is_improvement?: boolean;
}

const ROLE_COLORS: Record<string, string> = {
  PROCESSITEM: 'blue',
  PROCESSITEM_PERSISTENT: 'cyan',
  MATERIAL: 'green',
  RESOURCE: 'orange',
  CREATION: 'purple'
};

interface TransitionsProcessesViewProps {
  artifactId: string;
  artifactName?: string;
  onEditTransition?: (transitionId: string) => void;
  onEditProcess?: (processId: string) => void;
  hideDiagram?: boolean; // Hide the flowchart diagram, show only the list
}

export const TransitionsProcessesView: React.FC<TransitionsProcessesViewProps> = ({
  artifactId,
  artifactName,
  onEditTransition,
  onEditProcess,
  hideDiagram = false
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<TransitionProcessRow[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [generating, setGenerating] = useState<Set<string>>(new Set());
  const [states, setStates] = useState<any[]>([]);
  const [transitions, setTransitions] = useState<any[]>([]);
  const [showAddTransitionModal, setShowAddTransitionModal] = useState(false);
  const [form] = Form.useForm();
  const [improvementFilter, setImprovementFilter] = useState<'all' | 'operational' | 'improvement'>('all');
  
  // Dialog state
  const [showEditTransitionDialog, setShowEditTransitionDialog] = useState(false);
  const [showEditProcessDialog, setShowEditProcessDialog] = useState(false);
  const [editingTransitionId, setEditingTransitionId] = useState<string | null>(null);
  const [editingProcessId, setEditingProcessId] = useState<string | null>(null);
  
  // Validation state
  const [validationData, setValidationData] = useState<any>(null);
  const [violatedTransitionIds, setViolatedTransitionIds] = useState<Set<string>>(new Set());
  
  // Selection state for diagram elements
  const [selectedElement, setSelectedElement] = useState<{ type: 'state' | 'transition' | 'process'; id: string } | undefined>(undefined);

  useEffect(() => {
    loadData();
    loadValidation();
  }, [artifactId]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Get states for this artifact
      const statesResponse = await apiClient.get(`/prime-artifacts/${artifactId}/states`);
      setStates(statesResponse.data);
      
      // Get transitions for this artifact
      const transitionsResponse = await apiClient.get(`/prime-artifacts/${artifactId}/transitions`);
      const transitionsData = transitionsResponse.data;
      
      // Transform transitions for the diagram
      // Look up state names from loaded states using IDs
      const diagramTransitions = transitionsData.map((t: any) => {
        const fromStateId = t.from_state_id || t.from_state?.id || t.from_state_ref || null;
        const toStateId = t.to_state_id || t.to_state?.id || t.to_state_ref || null;
        
        // Look up state names from loaded states
        const fromState = fromStateId ? statesResponse.data.find((s: any) => String(s.id) === String(fromStateId)) : null;
        const toState = toStateId ? statesResponse.data.find((s: any) => String(s.id) === String(toStateId)) : null;
        
        const fromStateName = fromState?.name || t.from_state_name || t.from_state?.name || (fromStateId ? 'Missing State' : null);
        const toStateName = toState?.name || t.to_state_name || t.to_state?.name || 'Unknown State';
        
        return {
          id: t.id,
          name: t.name,
          from_state_id: fromStateId || '',
          to_state_id: toStateId || '',
          from_state_name: fromStateName || 'Unknown',
          to_state_name: toStateName || 'Unknown',
          transition_role: t.transition_type || t.default_transition_type || 'PROCESSITEM',
          process_name: null
        };
      });
      
      setTransitions(diagramTransitions);
      
      // Transform to combined view
      // Look up state names from loaded states using IDs
      const rows: TransitionProcessRow[] = transitionsData.map((t: any) => {
        const fromStateId = t.from_state_id || t.from_state?.id || t.from_state_ref || null;
        const toStateId = t.to_state_id || t.to_state?.id || t.to_state_ref || null;
        
        // Look up state names from loaded states
        const fromState = fromStateId ? statesResponse.data.find((s: any) => String(s.id) === String(fromStateId)) : null;
        const toState = toStateId ? statesResponse.data.find((s: any) => String(s.id) === String(toStateId)) : null;
        
        // Use state name from lookup, or fall back to API response fields
        let fromStateName = fromState?.name || t.from_state_name || t.from_state?.name;
        if (!fromStateName && fromStateId) {
          fromStateName = 'Missing State'; // State reference exists but state not found
        }
        if (!fromStateName) {
          fromStateName = null; // CREATION transitions have no from_state
        }
        
        let toStateName = toState?.name || t.to_state_name || t.to_state?.name;
        if (!toStateName) {
          toStateName = 'Unknown State';
        }
        
        // Handle both prime_process_ref and prime_process_id field names
        const processRef = t.prime_process_ref || t.prime_process_id || null;
        
        return {
          transition_id: t.id,
          transition_name: t.name || 'Unnamed Transition',
          from_state_name: fromStateName || 'Unknown',
          to_state_name: toStateName || 'Unknown',
          transition_role: t.transition_type || t.default_transition_type || 'PROCESSITEM',
          has_process: !!processRef,
          process_id: processRef,
          process_name: null, // Will be populated if we fetch process details
          process_type: null,
          is_improvement: t.is_improvement ?? false
        };
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[TransitionsProcessesView] Transformed rows:', rows.slice(0, 3)); // Log first 3 for debugging
      }
      
      // Set initial data first (without process names)
      setData(rows);
      if (process.env.NODE_ENV === 'development') {
        console.log('[TransitionsProcessesView] Set initial data rows:', rows.length);
      }
      
      // Fetch process names for those that have processes
      const processFetchPromises = rows.map(async (row) => {
        if (row.process_id) {
          try {
            const processResponse = await apiClient.get(`/prime-processes/${row.process_id}`);
            row.process_name = processResponse.data.name;
            row.process_type = processResponse.data.process_type;
          } catch (error: any) {
            // Process might be deleted or inaccessible - silently fail
          }
        }
      });
      
      // Wait for all process fetches to complete, then update state
      await Promise.all(processFetchPromises);
      
      // Update state with process names
      setData([...rows]); // Create new array to trigger re-render
    } catch (error: any) {
      console.error('[TransitionsProcessesView] Error loading transitions and processes:', error);
      message.error(`Failed to load data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadValidation = async () => {
    try {
      const response = await apiClient.get(`/prime-artifacts/${artifactId}/transitions/validation`);
      setValidationData(response.data);
      
      // Build set of violated transition IDs for quick lookup
      const violatedIds = new Set<string>();
      if (response.data.violations) {
        response.data.violations.forEach((violation: any) => {
          violation.transitions.forEach((t: any) => {
            violatedIds.add(t.id);
          });
        });
      }
      setViolatedTransitionIds(violatedIds);
    } catch (error: any) {
      console.error('Error loading validation:', error);
      // Don't show error message - validation is optional
    }
  };

  const generateProcess = async (transitionId: string) => {
    try {
      setGenerating(prev => new Set(prev).add(transitionId));
      console.log(`[TransitionsProcessesView] Generating process for transition: ${transitionId}`);
      const response = await apiClient.post(`/prime-transitions/${transitionId}/generate-process`, {});
      console.log(`[TransitionsProcessesView] Process generation response:`, response.data);
      
      // Check if the response includes the process reference
      // The API may return: prime_process_ref, process.id, or process_id
      const processRef = response.data.prime_process_ref || response.data.process?.id || response.data.process_id;
      if (processRef) {
        console.log(`[TransitionsProcessesView] Process reference in response: ${processRef}`);
        console.log(`[TransitionsProcessesView] Full response data:`, response.data);
        
        // If the transition wasn't automatically linked, we need to update it manually
        // Check if prime_process_ref is in the response (meaning it was linked)
        if (!response.data.prime_process_ref && processRef) {
          try {
            // Get the current transition to preserve other fields
            const transitionResponse = await apiClient.get(`/prime-artifacts/${artifactId}/transitions`);
            const transition = transitionResponse.data.find((t: any) => t.id === transitionId);
            
            if (transition) {
              // Update the transition with the process reference
              const updatePayload = {
                name: transition.name,
                description: transition.description,
                from_state_ref: transition.from_state_id || transition.from_state_ref,
                to_state_ref: transition.to_state_id || transition.to_state_ref,
                default_transition_type: transition.transition_type || transition.default_transition_type,
                prime_process_ref: processRef,
                demand_type: transition.demand_type,
                demand_flow_type: transition.demand_flow_type
              };

              await apiClient.put(`/prime-artifacts/${artifactId}/transitions/${transitionId}`, updatePayload);
            } else {
              console.warn(`[TransitionsProcessesView] Could not find transition ${transitionId} to update`);
            }
          } catch (linkError: any) {
            console.error(`[TransitionsProcessesView] Failed to link transition to process:`, linkError);
            // Don't fail the whole operation, just log the error
          }
        }
        
        // Immediately update the UI for this transition if we have the process reference
        setData(prevData => prevData.map(row => {
          if (row.transition_id === transitionId) {
            return {
              ...row,
              has_process: true,
              process_id: processRef,
              process_name: response.data.process?.name || response.data.process_name || null,
              process_type: response.data.process?.process_type || null
            };
          }
          return row;
        }));
      } else {
        console.warn(`[TransitionsProcessesView] No process reference found in response:`, response.data);
      }
      
      message.success(response.data.message || 'Process generated successfully');
      
      // Add a small delay to ensure backend has committed the changes
      // If the process was just created, give it more time to link the transition
      const delay = response.data.existing ? 500 : 1000;
      await new Promise(resolve => setTimeout(resolve, delay));

      // Reload to show the new process (this will fetch the full process details)
      await loadData(); // Reload to show the new process
      await loadValidation(); // Reload validation

      // After reload, check if the transition now has the process
      const updatedRows = data.filter(row => row.transition_id === transitionId);
      if (updatedRows.length > 0) {
        const updatedRow = updatedRows[0];
        if (!updatedRow.has_process && processRef) {
          console.warn(`[TransitionsProcessesView] WARNING: Process was generated (${processRef}) but transition is not linked after reload. This may indicate a backend issue.`);
        }
      }
    } catch (error: any) {
      message.error(`Failed to generate process: ${error.response?.data?.detail || error.message}`);
      console.error('Error generating process:', error);
    } finally {
      setGenerating(prev => {
        const next = new Set(prev);
        next.delete(transitionId);
        return next;
      });
    }
  };

  // Handlers for opening dialogs
  const handleEditTransition = (transitionId: string) => {
    setEditingTransitionId(transitionId);
    setShowEditTransitionDialog(true);
    onEditTransition?.(transitionId);
  };

  const handleEditProcess = (processId: string) => {
    setEditingProcessId(processId);
    setShowEditProcessDialog(true);
    onEditProcess?.(processId);
  };

  const handleCloseEditTransitionDialog = () => {
    setShowEditTransitionDialog(false);
    setEditingTransitionId(null);
    loadData(); // Reload data after editing
    loadValidation(); // Reload validation
  };

  const handleCloseEditProcessDialog = () => {
    setShowEditProcessDialog(false);
    setEditingProcessId(null);
    loadData(); // Reload data after editing
    loadValidation(); // Reload validation
  };

  const handleDeleteTransition = (transitionId: string, transitionName: string) => {
    Modal.confirm({
      title: 'Delete Transition',
      content: `Are you sure you want to delete the transition "${transitionName}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await apiClient.delete(`/prime-artifacts/${artifactId}/transitions/${transitionId}`);
          message.success('Transition deleted successfully');
          await loadData(); // Reload to reflect deletion
          await loadValidation(); // Reload validation
        } catch (error: any) {
          message.error(`Failed to delete transition: ${error.response?.data?.detail || error.message}`);
          console.error('Error deleting transition:', error);
        }
      }
    });
  };
  
  // Diagram element click handlers
  const handleStateClick = (stateId: string) => {
    setSelectedElement({ type: 'state', id: stateId });
    console.log('[TransitionsProcessesView] State clicked:', stateId);
    // Could scroll to state in list or show state details
  };
  
  const handleTransitionClick = (transitionId: string) => {
    setSelectedElement({ type: 'transition', id: transitionId });
    // Highlight in table
    const element = document.querySelector(`[data-transition-id="${transitionId}"]`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  
  const handleProcessClick = (processId: string) => {
    setSelectedElement({ type: 'process', id: processId });
    // Could open process details or scroll to it
  };

  const handleCreateTransition = async (values: any) => {
    let payload: any = null; // Declare outside try block for error logging
    
    try {
      console.log('Form values received:', values);
      
      // Validate required fields
      if (!artifactId) {
        message.error('Artifact ID is missing');
        return;
      }
      
      if (!values.transition_role) {
        message.error('Transition role is required');
        return;
      }
      
      if (!values.to_state) {
        message.error('To state is required');
        return;
      }
      
      // For non-CREATION transitions, from_state is required
      if (values.transition_role !== 'CREATION' && !values.from_state) {
        message.error('From state is required for non-CREATION transitions');
        return;
      }
      
      // Require user-provided name (no auto-generation)
      const transitionName = String(values.transition_name || '').trim();
      if (!transitionName) {
        message.error('Please enter a transition name');
        return;
      }
      
      // Build payload with all required fields - ensure all UUIDs are strings
      // Backend rejects CREATION with null UUID, so omit from_state_id entirely for CREATION
      const demandTypeLower = String(values.demand_type || "supply").toLowerCase();
      const demandFlowTypeLower = String(values.demand_flow_type || "pull").toLowerCase();
      
      payload = {
        name: String(transitionName || '').trim(),
        prime_artifact_id: String(artifactId).trim(),
        to_state_id: String(values.to_state).trim(),
        transition_type: String(values.transition_role).trim(),
        demand_type: demandTypeLower,
        demand_flow_type: demandFlowTypeLower
      };
      
      // Handle from_state_id based on transition type
      if (values.transition_role === 'CREATION') {
        // Backend currently requires the field; use to_state as fallback if none selected
        payload.from_state_id = String(values.from_state || values.to_state).trim();
      } else {
        // Non-CREATION transitions must have a from_state
        if (!values.from_state) {
          message.error('From state is required for this transition type');
          return;
        }
        payload.from_state_id = String(values.from_state).trim();
      }
      
      // Validate all UUIDs are valid format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(payload.prime_artifact_id)) {
        console.error('Invalid artifact ID format:', payload.prime_artifact_id);
        message.error('Invalid artifact ID format');
        return;
      }
      if (!uuidRegex.test(payload.to_state_id)) {
        console.error('Invalid to_state_id format:', payload.to_state_id);
        message.error('Invalid to state format');
        return;
      }
      if (payload.from_state_id && !uuidRegex.test(payload.from_state_id)) {
        console.error('Invalid from_state_id format:', payload.from_state_id);
        message.error('Invalid from state format');
        return;
      }
      
      // Include optional fields if provided
      if (values.description) {
        payload.description = values.description;
      }
      
      // Include is_improvement if provided
      if (values.is_improvement !== undefined) {
        payload.is_improvement = Boolean(values.is_improvement);
      }
      
      // Only include prime_process_ref if we have one
      if (values.prime_process_ref) {
        payload.prime_process_ref = String(values.prime_process_ref); // Ensure it's a string
      }
      
      // Log the payload to verify structure
      console.log('Creating transition with payload:', JSON.stringify(payload, null, 2));
      console.log('Payload keys:', Object.keys(payload));
      console.log('Payload values:', Object.values(payload));
      console.log('Artifact ID:', artifactId);
      console.log('Form values:', values);
      
      // Verify all required fields are present (using the field names the backend expects)
      const requiredFields = ['name', 'prime_artifact_id', 'to_state_id', 'transition_type', 'demand_type', 'demand_flow_type'];
      const missingFields = requiredFields.filter(field => {
        const value = payload[field];
        return value === undefined || value === null || value === '';
      });
      if (missingFields.length > 0) {
        console.error('Missing required fields in payload:', missingFields);
        console.error('Payload:', payload);
        message.error(`Missing required fields: ${missingFields.join(', ')}`);
        return;
      }
      
      // Verify from_state_id is handled correctly
      if (payload.transition_type !== 'CREATION' && !payload.from_state_id) {
        console.error('from_state_id is missing for non-CREATION transition');
        message.error('From state is required for this transition type');
        return;
      }
      
      const response = await apiClient.post(`/prime-artifacts/${artifactId}/transitions`, payload);
      
      console.log('Transition created successfully, response:', response.data);
      message.success('Transition created successfully');
      setShowAddTransitionModal(false);
      form.resetFields();
      
      // Reload data to show the new transition
      console.log('Reloading data after transition creation...');
      await loadData();
      console.log('Data reloaded after transition creation');
    } catch (error: any) {
      console.error('Full error details:', error.response?.data);
      console.error('Error detail array:', error.response?.data?.detail);
      
      // Log the actual error details for debugging
      if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        console.error('Detailed validation errors:', JSON.stringify(error.response.data.detail, null, 2));
        const missingFields = error.response.data.detail.map((err: any) => {
          const field = err.loc?.join('.') || err.msg || 'unknown';
          const msg = err.msg || 'missing';
          return `${field}: ${msg}`;
        }).filter(Boolean);
        console.error('Missing fields:', missingFields);
        message.error(`Validation errors: ${missingFields.join(', ')}`);
      } else if (error.response?.data?.detail) {
        console.error('Error detail (non-array):', error.response.data.detail);
        message.error(`Failed to create transition: ${JSON.stringify(error.response.data.detail)}`);
      } else {
        message.error(`Failed to create transition: ${error.message}`);
      }
      console.error('Error creating transition:', error);
      console.error('Request payload that was sent:', payload);
    }
  };

  const filteredData = data.filter(row => {
    // Filter by improvement status
    if (improvementFilter === 'improvement' && !row.is_improvement) return false;
    if (improvementFilter === 'operational' && row.is_improvement) return false;
    
    // Filter by search term
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      row.transition_name.toLowerCase().includes(term) ||
      row.from_state_name.toLowerCase().includes(term) ||
      row.to_state_name.toLowerCase().includes(term) ||
      (row.process_name && row.process_name.toLowerCase().includes(term))
    );
  });

  const missingProcessCount = data.filter(row => !row.has_process).length;

  const columns: ColumnsType<TransitionProcessRow> = [
    {
      title: 'Transition',
      key: 'transition',
      width: '35%',
      sorter: (a, b) => a.transition_name.localeCompare(b.transition_name),
      sortDirections: ['ascend', 'descend'],
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>
            {violatedTransitionIds.has(record.transition_id) && (
              <Tooltip title="Duplicate process association detected">
                <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
              </Tooltip>
            )}
            {record.transition_name}
            {record.is_improvement && (
              <Tag color="purple" style={{ marginLeft: 8 }}>Improvement</Tag>
            )}
          </div>
          <div style={{ fontSize: 13, color: '#666' }}>
            <ArrowRightOutlined style={{ marginRight: 4 }} />
            {record.from_state_name === 'null' || record.from_state_name === null ? (
              <>
                <Tag color="purple" style={{ marginRight: 4 }}>CREATED</Tag>
                → {record.to_state_name}
              </>
            ) : (
              <>
                {record.from_state_name} → {record.to_state_name}
              </>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'transition_role',
      key: 'role',
      width: '15%',
      sorter: (a, b) => a.transition_role.localeCompare(b.transition_role),
      sortDirections: ['ascend', 'descend'],
      render: (role: string) => (
        <Tag color={ROLE_COLORS[role] || 'default'}>
          {role}
        </Tag>
      ),
    },
    {
      title: 'Process',
      key: 'process',
      width: '35%',
      sorter: (a, b) => (a.process_name || '').localeCompare(b.process_name || ''),
      sortDirections: ['ascend', 'descend'],
      render: (_, record) => {
        if (!record.has_process) {
          return (
            <div style={{ color: '#999', fontStyle: 'italic' }}>
              <WarningOutlined style={{ marginRight: 4, color: '#faad14' }} />
              No process assigned
            </div>
          );
        }
        
        return (
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>
              {record.process_name || 'Loading...'}
            </div>
            {record.process_type && (
              <Tag color="blue" style={{ fontSize: 11 }}>
                {record.process_type}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '20%',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Transition">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditTransition(record.transition_id)}
            />
          </Tooltip>
          
          {record.has_process ? (
            <Tooltip title="Edit Process">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditProcess(record.process_id!)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Generate Process">
              <Button
                size="small"
                type="primary"
                icon={<ThunderboltOutlined />}
                loading={generating.has(record.transition_id)}
                onClick={() => generateProcess(record.transition_id)}
              >
                Generate
              </Button>
            </Tooltip>
          )}
          
          <Tooltip title="Delete Transition">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteTransition(record.transition_id, record.transition_name)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading transitions and processes...</div>
      </div>
    );
  }

  return (
    <div className={styles.transitionsProcessesView}>
      {/* State Flow Diagram - conditionally hidden */}
      {!hideDiagram && (
        <>
          <ArtifactStateFlowDiagram
            states={states}
            transitions={transitions}
            artifactName={artifactName || 'Artifact'}
            artifactId={artifactId}
            onStateClick={handleStateClick}
            onTransitionClick={handleTransitionClick}
            onProcessClick={handleProcessClick}
            selectedElement={selectedElement}
          />
          <Divider />
        </>
      )}

      {/* Validation Warnings */}
      {validationData && validationData.has_violations && (
        <Alert
          type="warning"
          showIcon
          message="Duplicate Process Associations Detected"
          description={
            <div>
              <p>Multiple transitions are associated with the same process. Each artifact should only have one transition per process.</p>
              <ul style={{ marginBottom: 0 }}>
                {validationData.violations.map((violation: any, idx: number) => (
                  <li key={idx}>
                    <strong>{violation.process_name}</strong>: {violation.transition_count} transitions
                    <ul>
                      {violation.transitions.map((t: any) => (
                        <li key={t.id}>
                          {t.name} ({t.role})
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
              <p style={{ marginTop: 8, marginBottom: 0 }}>
                <strong>To fix:</strong> Edit each transition and change or remove the process assignment to ensure only one transition per process.
              </p>
            </div>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0, marginBottom: 8 }}>
            Transition Details
          </h3>
          <Space>
            <Badge count={data.length} style={{ backgroundColor: '#1890ff' }} />
            {missingProcessCount > 0 && (
              <Badge 
                count={`${missingProcessCount} missing processes`} 
                style={{ backgroundColor: '#faad14' }} 
              />
            )}
          </Space>
        </div>
        
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowAddTransitionModal(true)}
          >
            Add Transition
          </Button>
          <Select
            value={improvementFilter}
            onChange={(value) => setImprovementFilter(value)}
            style={{ width: 150 }}
          >
            <Select.Option value="all">All Transitions</Select.Option>
            <Select.Option value="operational">Operational</Select.Option>
            <Select.Option value="improvement">Improvement</Select.Option>
          </Select>
          <Search
            placeholder="Search transitions or processes..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="transition_id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} items`,
        }}
        size="middle"
      />

      {/* Add Transition Modal */}
      <Modal
        title="Create New Transition"
        open={showAddTransitionModal}
        onCancel={() => {
          setShowAddTransitionModal(false);
          form.resetFields();
        }}
        onOk={async () => {
          try {
            // Get all form values first
            const formValues = await form.validateFields();
            console.log('Form validation passed, values:', formValues);
            // Submit the form
            form.submit();
          } catch (error) {
            console.error('Form validation failed:', error);
            message.error('Please fix the form errors before submitting');
          }
        }}
        okText="Create"
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateTransition}
        >
          <Form.Item
            label="Transition Name"
            name="transition_name"
            rules={[{ required: true, message: 'Please enter a transition name' }]}
          >
            <Input placeholder="Enter transition name" />
          </Form.Item>

          <Form.Item
            label="Transition Role"
            name="transition_role"
            rules={[{ required: true, message: 'Please select a transition role' }]}
            initialValue="PROCESSITEM"
          >
            <Select onChange={() => form.validateFields(['from_state'])}>
              <Select.Option value="PROCESSITEM">Process Item (main process)</Select.Option>
              <Select.Option value="MATERIAL">Material (consumption)</Select.Option>
              <Select.Option value="RESOURCE">Resource (usage)</Select.Option>
              <Select.Option value="CREATION">Creation (new artifact)</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="From State"
            name="from_state"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const transitionRole = getFieldValue('transition_role');
                  console.log('Validating from_state:', { value, transitionRole });
                  
                  if (transitionRole === 'CREATION') {
                    // From State is optional for CREATION transitions
                    console.log('CREATION transition - from_state is optional');
                    return Promise.resolve();
                  }
                  
                  if (!value) {
                    console.log('Non-CREATION transition requires from_state');
                    return Promise.reject(new Error('Please select a from state'));
                  }
                  
                  console.log('From_state validation passed');
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Select 
              placeholder="Select starting state (not required for Creation)" 
              allowClear
            >
              {states.map(state => (
                <Select.Option key={state.id} value={state.id}>
                  {state.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="To State"
            name="to_state"
            rules={[{ required: true, message: 'Please select a to state' }]}
          >
            <Select placeholder="Select destination state">
              {states.map(state => (
                <Select.Option key={state.id} value={state.id}>
                  {state.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Demand Type"
            name="demand_type"
            rules={[{ required: true, message: 'Please select a demand type' }]}
            initialValue="supply"
          >
            <Select>
              <Select.Option value="supply">Supply</Select.Option>
              <Select.Option value="target">Target</Select.Option>
              <Select.Option value="capacity">Capacity</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Demand Flow Type"
            name="demand_flow_type"
            rules={[{ required: true, message: 'Please select a demand flow type' }]}
            initialValue="pull"
          >
            <Select>
              <Select.Option value="pull">Pull</Select.Option>
              <Select.Option value="drum">Drum</Select.Option>
              <Select.Option value="push">Push</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Description (Optional)"
            name="description"
          >
            <Input.TextArea 
              rows={3}
              placeholder="Enter transition description"
            />
          </Form.Item>

          <Form.Item
            label="Is Improvement"
            name="is_improvement"
            valuePropName="checked"
            tooltip="Mark this transition as part of a continuous improvement workflow, separate from operational workflows"
          >
            <Checkbox>Is Improvement</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Transition Dialog */}
      <EditTransitionDialog
        visible={showEditTransitionDialog}
        transitionId={editingTransitionId}
        artifactId={artifactId}
        onClose={handleCloseEditTransitionDialog}
        onSave={handleCloseEditTransitionDialog}
      />

      {/* Edit Process Dialog */}
      <EditProcessDialog
        visible={showEditProcessDialog}
        processId={editingProcessId}
        artifactId={artifactId}
        onClose={handleCloseEditProcessDialog}
        onSave={handleCloseEditProcessDialog}
      />
    </div>
  );
};

